import { ErrorMessage } from '@hookform/error-message';
import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { CollapsiblePanel } from '@ge/components/collapsible-panel';
import { DataLoader } from '@ge/components/data-loader';
import { Icon, Icons } from '@ge/components/icon';
import { Dialog } from '@ge/components/modal';
import { ScrollingContainer } from '@ge/components/scrolling-container';
import { Select } from '@ge/components/select';
import { Severity } from '@ge/components/severity';
import { useColumnState } from '@ge/components/table/use-column-state';
import { Text } from '@ge/components/typography';
import { useTurbineTypes } from '@ge/feat-inspections/data-hooks/use-app';
import { useCreateNewDamages, useEditDamages } from '@ge/feat-inspections/data-hooks/use-damages';
import { useDefectTree } from '@ge/feat-inspections/data-hooks/use-parameters';
import { useSSO } from '@ge/feat-inspections/data-hooks/use-session';
import {
  ExistingDamageDetailsColumnDefs,
  defaultExistingDamageDetailsCols,
} from '@ge/feat-inspections/models/existing-damage-details-col';
import {
  EntityType,
  DataLoaderType,
  DamagesField,
  Capability,
  PermissionScope,
  DamageDiscription,
  DayJs,
  DamgesStatus,
  SortDirection,
  DefaultRoleType,
} from '@ge/models/constants';
import { TextMetaField, DateMetaField } from '@ge/shared/components/meta-fields';
import { TaskProvider } from '@ge/shared/components/tasks/task-provider';
import { StyledDateContainer } from '@ge/shared/components/tasks/task-template-shared';
import { useAuth } from '@ge/shared/data-hooks';
import { getUserRolesFromAccessToken } from '@ge/shared/services/auth';
import { globalColors, typography } from '@ge/tokens';
import { StatusColor } from '@ge/tokens/colors/colors';
import { sorter } from '@ge/util/metric-sorter';
import { HeaderIcon } from '@ge/web-client/src/app/components/entity-details/entity-details-header';
import { DetailsCurrentSeparator } from '@ge/web-client/src/app/components/entity-details/entity-details-shared';

import { ExistingDamageDetailsTable } from '../../inspections/components/tables/existing-damage-details-table';
import { useExistingDamageDetails } from '../../inspections/data-hooks/use-existing-damage-details';

const StyledDialog = styled(Dialog)`
  max-height: 540px;
  width: 550px;
  margin-top: 15px;
  header {
    border-radius: 4px 4px 0 0;
  }
  section {
    background: ${(props) => props.theme.malAdjustmentTable.malStatusModal.modalBgColor};
    padding: 10px 15px;
    max-height: 400px;
    overflow-y: scroll;
    &::-webkit-scrollbar {
      width: 8px;
      height: 0px;
    }
    &::-webkit-scrollbar-track {
      background: ${(props) => props.theme.scrollbar.trackBackground};
    }
    &::-webkit-scrollbar-thumb {
      background: ${(props) => props.theme.scrollbar.thumbBackground};
      border-radius: 2.5px;
    }
    &::-webkit-scrollbar-thumb:active {
      background: ${(props) => props.theme.scrollbar.trackActiveBackground};
    }
  }
  footer {
    border-radius: 0 0 4px 4px;
    background: ${(props) => props.theme.malAdjustmentTable.malStatusModal.modalBgColor};
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: end;
`;

const HeaderDetails = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.entityDetails.headerIconColor};
`;

const CancelButton = styled(Button)`
  width: 75px;
  height: 24px;
  margin-right: 4px;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.malAdjustmentTable.malStatusModal.cancelBtnBorderColor};
  background: ${(props) => props.theme.malAdjustmentTable.malStatusModal.cancelBtnBgColor};
  color: ${(props) => props.theme.malAdjustmentTable.malStatusModal.cancelBtnColor};
  font-size: 13px;
  font-weight: 600;
  line-height: 15.6px;
  text-align: center;
`;

const ConfirmButton = styled(Button)`
  width: 83px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid
    ${(props) => props.theme.malAdjustmentTable.malStatusModal.confirmBtnBorderColor};
  background: ${(props) => props.theme.malAdjustmentTable.malStatusModal.confirmBtnBgColor};
  color: ${(props) => props.theme.malAdjustmentTable.malStatusModal.confirmBtnColor};
  font-size: 13px;
  font-weight: 600;
  line-height: 15.6px;
  text-align: center;
`;

const StyledDetailsCurrentSeparator = styled(DetailsCurrentSeparator)`
  color: ${(props) => props.theme.entityDetails.headerIconColor};
`;

const SiteIcon = styled(Icon).attrs(({ theme }) => ({
  size: 12,
  icon: Icons.SITE,
  color: theme.createReport.sidebar.siteIconColor,
  viewbox: '0 0 20 20',
}))`
  display: inline-block;
  position: relative;
  margin-right: 6px;
`;

const CaseDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  .title {
    display: flex;
    align-items: self-end;
    gap: 4px;
  }
`;

const DamageDialogDivider = styled.hr`
  box-sizing: border-box;
  border: 1px solid ${(props) => props.theme.malAdjustmentTable.malStatusModal.tableDividerColor};
  margin-top: 10px;
  margin-bottom: 10px;
`;

const ButtonWrapper = styled.span`
  display: flex;
  align-items: self-start;
  gap: 10px;
`;

const AddButtonWrapper = styled.span`
  display: flex;
  align-items: self-start;
  gap: 10px;
  justify-content: end;
`;

const DeleteButtonWrapper = styled.div`
  display: block;
  align-items: self-start;
  gap: 10px;
  justify-content: end;
  position: absolute;
  left: 96%;
`;

const NewDamageButton = styled.button`
  font-weight: 600;
  color: ${(props) => props.theme.malAdjustmentTable.approveRejectSelectionSection.btntextColor};
  font-size: 11px;
  letter-spacing: 0;
  padding: 0;
`;

const AddButton = styled.button`
  font-weight: 600;
  color: ${(props) => props.theme.malAdjustmentTable.approveRejectSelectionSection.btntextColor};
  font-size: 11px;
  letter-spacing: 0;
  padding: 0;
`;

const AddIcon = styled(Icon).attrs((props) => ({
  size: 12,
  color: props.theme.dataExplorer.header.addIconColor,
}))`
  flex-shrink: 0;
`;

const DeleteIcon = styled(Icon).attrs((props) => ({
  size: 16,
  color: props.theme.dataExplorer.header.addIconColor,
}))`
  flex-shrink: 0;
`;

const NewDamageContainer = styled.div`
  background: ${(props) => props.theme.entityDetails.cases.details.addDamagePanelTableBackground};
  width: 100%;
  margin-top: 20px;
  margin-bottom: 10px;
  padding: 10px 0 10px 0;
  position: relative;
`;

const AddDamageFormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 12px;
  padding: 10px 65px 0 15px;
  .pac-url {
    width: 260px;
  }
`;

export const EntityCollapsiblePanel = styled(CollapsiblePanel)`
  margin-bottom: 20px;
  background: ${(props) => props.theme.entityDetails.cases.details.addDamagePanelTableBackground};
  width: -webkit-fill-available;
  .title {
    border: 0;
    margin-bottom: 0.5rem;
    margin-top: 0.5rem;
    svg {
      fill: ${(props) => props.theme.entityDetails.cases.details.collapseArrow};
    }
    h2 {
      font-size: 18px;
      line-height: 21px;
      &.add-margin {
        margin-top: 10px;
      }
    }
  }

  table {
    tbody tr:nth-child(odd) {
      background: ${(props) =>
        props.theme.entityDetails.cases.details.addDamagePanelTableBackground}; !important;
    }

    tbody tr:nth-child(even) {
      background: ${(props) =>
        props.theme.entityDetails.cases.details.addDamagePanelTableBackground}; !important;
    }

    tr:first-child th {
      border-right: 1px solid #252c33;
    }

    thead tr,
    th {
      border-bottom: 1px solid #252c33;
      border-right: 1px solid #252c33;
    }

    tbody tr,
    td {
      border-bottom: 1px solid #252c33 !important;
      border-right: 1px solid #252c33 !important;
    }
  }

  .collapsible-panel-content.no-overflow {
    &.expanded {
      max-height: 200px !important;
      transition: max-height 0s ease;
    }
    overflow-y: scroll;
    overflow-x: scroll;
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background: ${(props) => props.theme.scrollbar.trackBackground};
    }
    &::-webkit-scrollbar-thumb {
      background: ${(props) => props.theme.scrollbar.thumbBackground};
      // border-radius: 2.5px;
    }
    &::-webkit-scrollbar-thumb:active {
      background: ${(props) => props.theme.scrollbar.trackActiveBackground};
    }
  }
`;

const SectionContainer = styled.div`
  position: relative;
  display: flex;
`;

const StyledSelect = styled(Select)`
  width: 130px;
  height: 25px;
  .select__control {
    max-height: 25px;
    align-content: center;
    div:first-of-type {
      max-height: 25px;
      max-width: 130px;
      width: 130px;
    }
    .select__indicators {
      width: 25px;
    }
  }
  .select__menu {
    .select__menu-list {
      max-height: 175px;
      &::-webkit-scrollbar {
        width: 4px;
        height: 0px;
      }
      &::-webkit-scrollbar-track {
        background: ${(props) => props.theme.scrollbar.trackBackground};
      }
      &::-webkit-scrollbar-thumb {
        background: ${(props) => props.theme.scrollbar.thumbBackground};
        border-radius: 2.5px;
      }
    }
  }
`;

const DialogScrollWrapper = styled.div`
  box-sizing: border-box;
  min-height: 600px;
  padding: 20px 25px;
`;

const StyledFormDateContainer = styled(StyledDateContainer)`
  margin-right: 0px;
  .date-picker {
    .task-dialog-date {
      position: absolute;
      bottom: 7px;
    }
    input {
      width: 120px;
    }
  }
`;

const StyledErrorMessage = styled.span`
  text-indent: -1.5em;
  color: ${StatusColor.DANGER};
  margin-top: 5px;

  &:before {
    content: '\\2296'; // &ominus;
    font-size: 13px;
    padding-right: 2px;
  }
`;

const DataLoaderContainer = styled.div`
  margin: 0 auto;

  img {
    height: 25vh;
  }
`;

const StyledLabel = styled.label`
  color: ${(props) => props.theme.select.labelTextColor};
  font-size: 11px;
  font-family: 'Museo Sans';
  font-weight: 500;
  line-height: 13px;
  margin-bottom: 5px;
`;

const StyledCategoryContainer = styled.div`
  gap: 5px;
  display: flex;
  flex-direction: column;
  font-size: 13px;
`;

const ErrorText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 2,
}))`
  color: ${(props) => props.theme.dangerColor} !important;
  display: block;
  margin-bottom: 10px;
  width: 190px;
  span {
    margin: 3px 0;
  }
`;

export const PacWrapper = styled.div`
  position: relative;
`;

export const Cat3Wrapper = styled.div`
  position: relative;
`;

const setInitialSelectValue = (val) => {
  return val ? { value: val, label: val } : undefined;
};

//This contains the form fields for new damage dialog, need to maintain this array for delete functionality
const FORM_FIELDS = [
  'status',
  'category',
  'component',
  'location',
  'part',
  'damage',
  'followupAction',
  'rulStartDate',
  'followupDate',
  'pacNumber',
  'pacUrl',
  'cat3DispositionDate',
  'cat3aDispositionDate',
  'cat4DispositionDate',
  'cat5DispositionDate',
];
const AddDamageDialog = ({ onClose, caseDetail, entityType, isUpdate, exisitingDamage, saveCalmCaseEscaltion }) => {
  const { t } = useTranslation(['analyze.manual-adjustment'], { useSuspense: false });
  const { isAuthorized } = useAuth();
  const isDamageAuthorized = isAuthorized({
    capabilities: [
      { capability: Capability.CASES_RECOMMENDATIONS, scopes: [PermissionScope.CREATE] },
    ],
  });
  const [showNewDamageDialog, setShowNewDamageDialog] = useState(false);
  const [newDamagesCounter, setNewDamagesCounter] = useState(1);
  const [selectedComponent, setSelectedComponent] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedPart, setSelectedPart] = useState('');
  const [componentValue, setComponentValue] = useState([]);
  const [partValue, setPartValue] = useState([]);
  const [selectedLocationOption, setselectedLocationOption] = useState([]);
  const [selectedPartOption, setselectedPartOption] = useState([]);
  const [selectedDamageOption, setselectedDamageOption] = useState([]);
  const [category, setCategory] = useState([]);
  const [userRole, setUserRole] = useState([]);
  const defaultFormValues = useMemo(
    () => ({
      ...(exisitingDamage
        ? {
            status0: setInitialSelectValue(exisitingDamage.status),
            category0: setInitialSelectValue(exisitingDamage.category),
            component0: setInitialSelectValue(exisitingDamage.component),
            location0: setInitialSelectValue(exisitingDamage.location),
            part0: setInitialSelectValue(exisitingDamage.part),
            followupAction0: setInitialSelectValue(exisitingDamage.followupAction),
            followupDate0:
              exisitingDamage && exisitingDamage.followupDate
                ? new Date(exisitingDamage.followupDate * 1000)
                : null,
            rulStartDate0:
              exisitingDamage && exisitingDamage.rulStartDate
                ? new Date(exisitingDamage.rulStartDate * 1000)
                : null,
            damage0: !DamageDiscription.includes(exisitingDamage.damage)
              ? setInitialSelectValue('Unknown')
              : setInitialSelectValue(exisitingDamage.damage),
            pacNumber0: exisitingDamage.pacNumber,
            pacUrl0: exisitingDamage.pacUrl,
            cat3DispositionDate0:
              exisitingDamage && exisitingDamage.cat3DispositionDate
                ? new Date(exisitingDamage.cat3DispositionDate * 1000)
                : null,
            cat3aDispositionDate0:
              exisitingDamage && exisitingDamage.cat3aDispositionDate
                ? new Date(exisitingDamage.cat3aDispositionDate * 1000)
                : null,
            cat4DispositionDate0:
              exisitingDamage && exisitingDamage.cat4DispositionDate
                ? new Date(exisitingDamage.cat4DispositionDate * 1000)
                : null,
            cat5DispositionDate0:
              exisitingDamage && exisitingDamage.cat5DispositionDate
                ? new Date(exisitingDamage.cat5DispositionDate * 1000)
                : null,
          }
        : {}),
    }),
    [exisitingDamage],
  );

  const { formState, handleSubmit, watch, ...methods } = useForm({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });
  const [selectedExistingDamages, setSelectedExistingDamages] = useState([]);
  const { data: sso = null } = useSSO();
  const { data: allTurbineTypes = null } = useTurbineTypes();
  const { visibleCols } = useColumnState({
    columnDefs: ExistingDamageDetailsColumnDefs,
    defaultCols: defaultExistingDamageDetailsCols,
  });

  const { data: existingDamageData, isLoading } = useExistingDamageDetails({
    assetId: caseDetail?.asset?.id,
  });

  const { data: defectTreeData = [], isLoading: isDefectTreeLoading } = useDefectTree({
    inspectionType: 'caseManual',
  });

  const modelStarts2x3x =
    caseDetail &&
    caseDetail?.site?.turbineTypes.some(
      (turbine) => turbine.model.startsWith('2.') || turbine.model.startsWith('3.'),
    );

  const {
    createDamages,
    isLoading: isAddSaveLoading,
    error,
  } = useCreateNewDamages({
    onSuccess: () => {
      saveCalmCaseEscaltion?.();
      onClose();
    },
    onError: (error) => {
      console.error('Failed to create damages:', error);
      setShowNewDamageDialog(true);
    },
    caseId: caseDetail.id,
  });
  const {
    editDamages,
    isLoading: isEditSaveLoading,
    error: updateError,
  } = useEditDamages({
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      console.error('Failed to update damages:', error);
      setShowNewDamageDialog(true);
    },
    caseId: caseDetail.id,
  });

  useEffect(() => {
    if (isUpdate) {
      setShowNewDamageDialog(true);
    }
  }, [isUpdate]);

  useEffect(() => {
    if (isUpdate) {
      const initialCategory = {
        value: exisitingDamage?.category,
        label: exisitingDamage?.category,
      };

      setCategory((prev) => [...prev, initialCategory]);
    }
  }, [exisitingDamage?.category]);

  useEffect(() => {}, [category]);

  const normalizeRoleName = (roleName = '') => {
    return roleName.split('-').pop()?.trim();
  };

  useEffect(() => {
    let filteredRoles = [];
    const roles = getUserRolesFromAccessToken(); // array of role names
    if (Array.isArray(roles)) {
      filteredRoles = roles
        .map(normalizeRoleName)
        .filter(Boolean)
        .filter(
          (roleName) =>
            roleName !== DefaultRoleType.MONITOR_ADMIN &&
            roleName !== DefaultRoleType.ANALYTICS_VIEW &&
            roleName !== DefaultRoleType.MONITOR_VIEW,
        );
    }
    setUserRole(filteredRoles);
  }, []);

  useEffect(() => {}, [userRole]);

  const icon = useMemo(() => {
    if (!caseDetail) return null;
    if (EntityType.CASE === entityType) {
      return caseDetail.childCases?.length ? Icons.CASE : Icons.ANOMALY;
    }
    if (typeof caseDetail.caseId !== 'undefined') {
      return Icons.CASE;
    }
    return Icons[entityType.toUpperCase()];
  }, [caseDetail, entityType]);

  const getOptions = (field) => {
    const data = defectTreeData?.items?.find((x) => x.access === field);

    if (!data || !data.values) {
      return [];
    }

    const mappedValues = data.values.map((value) => ({
      value: value,
      label: t(value, value.replace(/[_-]/g, ' ')),
    }));
    const sortedValues = mappedValues.sort((a, b) => a.label.localeCompare(b.label));

    return sortedValues;
  };

  const isEngineeringAdmin = userRole?.includes('Engineering_Admin');
  const isEngineer = userRole?.includes('Engineer');
  const isCategoryHigherThan3 = (selectedCategory) =>
    ['Category 3', 'Category 3a', 'Category 4', 'Category 5'].includes(selectedCategory);
  const canEngineerUpdateEmptyRul = (selectedCategory) =>
    isDamageAuthorized &&
    isEngineer &&
    !isEngineeringAdmin &&
    !exisitingDamage?.rulStartDate &&
    isCategoryHigherThan3(selectedCategory);

  const validateDates = () => {
    let validate = false;
    const categoryRank = {
      'Category 2': 2,
      'Category 3': 3,
      'Category 3a': 3.1,
      'Category 4': 4,
      'Category 5': 5,
    };

    [...Array(newDamagesCounter)].forEach((e, i) => {
      const selectedCategory =
        methods.getValues(`category${i}`)?.value || category[i]?.value || exisitingDamage?.category;
      const visibleCategoryFields = getFieldsToShow(exisitingDamage?.category);
      if (
        isUpdate &&
        isDamageAuthorized &&
        isEngineer &&
        categoryRank[selectedCategory] < categoryRank[exisitingDamage.category]
      ) {
        methods.setError(`category${i}`, {
          type: 'manual',
          message: t(
            'damage.form.category_error_msg',
            'Damage category cannot be downgraded. Please contact an Engineering Admin',
          ),
        });
        validate = true;
      }

      if (
        isDamageAuthorized &&
        (isEngineeringAdmin || canEngineerUpdateEmptyRul(selectedCategory)) &&
        !methods.getValues(`rulStartDate${i}`) &&
        selectedCategory !== 'Category 2'
      ) {
        methods.setError(`rulStartDate${i}`, DayJs.REQUIRED);
        validate = true;
      }
      if (
        !isUpdate &&
        !methods.getValues(`rulStartDate${i}`) &&
        category[i]?.value !== 'Category 2'
      ) {
        methods.setError(`rulStartDate${i}`, DayJs.REQUIRED);
        validate = true;
      }

      if (
        isUpdate &&
        isDamageAuthorized &&
        isEngineeringAdmin &&
        visibleCategoryFields.includes('Category 3') &&
        selectedCategory === 'Category 3' &&
        !methods.getValues(`cat3DispositionDate${i}`)
      ) {
        methods.setError(`cat3DispositionDate${i}`, DayJs.REQUIRED);
        validate = true;
      }
      if (
        isUpdate &&
        isDamageAuthorized &&
        isEngineeringAdmin &&
        visibleCategoryFields.includes('Category 3a') &&
        selectedCategory === 'Category 3a' &&
        !methods.getValues(`cat3aDispositionDate${i}`)
      ) {
        methods.setError(`cat3aDispositionDate${i}`, DayJs.REQUIRED);
        validate = true;
      }
      if (
        isUpdate &&
        isDamageAuthorized &&
        isEngineeringAdmin &&
        visibleCategoryFields.includes('Category 4') &&
        selectedCategory === 'Category 4' &&
        !methods.getValues(`cat4DispositionDate${i}`)
      ) {
        methods.setError(`cat4DispositionDate${i}`, DayJs.REQUIRED);
        validate = true;
      }
      if (
        isUpdate &&
        isDamageAuthorized &&
        isEngineeringAdmin &&
        visibleCategoryFields.includes('Category 5') &&
        selectedCategory === 'Category 5' &&
        !methods.getValues(`cat5DispositionDate${i}`)
      ) {
        methods.setError(`cat5DispositionDate${i}`, DayJs.REQUIRED);
        validate = true;
      }
    });
    return validate;
  };

  const onSubmit = useCallback(
    (values) => {
      if (Object.keys(values).length && validateDates()) return;
      const getTurbineTypeIdByName = (model) => {
        return allTurbineTypes?.find((turb) => turb.attributes.turbinetype === model)?.id;
      };

      const newDamages = [];

      if (values[`status${0}`]?.value) {
        for (let index = 0; index < newDamagesCounter; index++) {
          const isCat3DispositionVisible =
            isUpdate && getFieldsToShow(exisitingDamage?.category).includes('Category 3');
          const isCat3aDispositionVisible =
            isUpdate && getFieldsToShow(exisitingDamage?.category).includes('Category 3a');
          const isCat4DispositionVisible =
            isUpdate && getFieldsToShow(exisitingDamage?.category).includes('Category 4');
          const isCat5DispositionVisible =
            isUpdate && getFieldsToShow(exisitingDamage?.category).includes('Category 5');

          newDamages.push({
            status: values[`status${index}`]?.value,
            category: values[`category${index}`]?.value,
            component: values[`component${index}`]?.value,
            location: values[`location${index}`]?.value,
            part:
              values[`component${index}`]?.value === 'Generator'
                ? ''
                : values[`part${index}`]?.value,
            // If others is selected from UI then free flowing text, else value from dropdown
            damage: values[`damage${index}`]?.value,
            followupAction: values[`followupAction${index}`]?.value,
            followupDate: values[`followupDate${index}`]
              ? new Date(values[`followupDate${index}`]).getTime() / 1000
              : null,
            rulStartDate: values[`rulStartDate${index}`]
              ? new Date(values[`rulStartDate${index}`]).getTime() / 1000
              : exisitingDamage?.rulStartDate
              ? exisitingDamage?.rulStartDate
              : null,
            pacNumber: values[`pacNumber${index}`].trim() || '',
            pacUrl: values[`pacUrl${index}`].trim() || '',
            cat3DispositionDate: values[`cat3DispositionDate${index}`]
              ? new Date(values[`cat3DispositionDate${index}`]).getTime() / 1000
              : isCat3DispositionVisible
              ? null
              : exisitingDamage?.cat3DispositionDate,
            cat3aDispositionDate: values[`cat3aDispositionDate${index}`]
              ? new Date(values[`cat3aDispositionDate${index}`]).getTime() / 1000
              : isCat3aDispositionVisible
              ? null
              : exisitingDamage?.cat3aDispositionDate,
            cat4DispositionDate: values[`cat4DispositionDate${index}`]
              ? new Date(values[`cat4DispositionDate${index}`]).getTime() / 1000
              : isCat4DispositionVisible
              ? null
              : exisitingDamage?.cat4DispositionDate,
            cat5DispositionDate: values[`cat5DispositionDate${index}`]
              ? new Date(values[`cat5DispositionDate${index}`]).getTime() / 1000
              : isCat5DispositionVisible
              ? null
              : exisitingDamage?.cat5DispositionDate,
          });
        }
      }

      if (isUpdate) {
        editDamages(
          newDamages.map((d) => {
            return {
              ...d,
              id: exisitingDamage.id,
              category: isDamageAuthorized ? d.category : exisitingDamage.category,
              rulStartDate:
                isDamageAuthorized && (isEngineeringAdmin || canEngineerUpdateEmptyRul(d.category))
                  ? d.rulStartDate
                  : exisitingDamage.rulStartDate || null,
              cat3DispositionDate:
                isDamageAuthorized && isEngineeringAdmin
                  ? d.cat3DispositionDate
                  : exisitingDamage.cat3DispositionDate,
              cat3aDispositionDate:
                isDamageAuthorized && isEngineeringAdmin
                  ? d.cat3aDispositionDate
                  : exisitingDamage.cat3aDispositionDate,
              cat4DispositionDate:
                isDamageAuthorized && isEngineeringAdmin
                  ? d.cat4DispositionDate
                  : exisitingDamage.cat4DispositionDate,
              cat5DispositionDate:
                isDamageAuthorized && isEngineeringAdmin
                  ? d.cat5DispositionDate
                  : exisitingDamage.cat5DispositionDate,
            };
          }),
        );
      } else {
        createDamages({
          new: {
            turbine_number: caseDetail.asset?.name,
            turbinetype_lid: getTurbineTypeIdByName(caseDetail.asset?.model),
            uploader_lid: sso,
            auto_execute: 'false',
            site_lid: caseDetail.asset?.site?.id,
            asset_id: caseDetail.asset?.id,
            inspection_type: 'Manual',
            damages: newDamages,
            creation_date: Math.floor(new Date().getTime() / 1000),
          },
          existing: selectedExistingDamages,
        });
      }
    },
    [
      isUpdate,
      allTurbineTypes,
      newDamagesCounter,
      editDamages,
      exisitingDamage,
      selectedExistingDamages,
      createDamages,
      caseDetail.asset?.name,
      caseDetail.asset?.model,
      caseDetail.asset?.site?.id,
      caseDetail.asset?.id,
      sso,
      category,
      isEngineeringAdmin,
      isDamageAuthorized,
      validateDates,
      canEngineerUpdateEmptyRul,
    ],
  );

  const trackedFieldNames = useMemo(
    () => [
      'status0',
      'category0',
      'component0',
      'location0',
      'part0',
      'damage0',
      'followupAction0',
      'followupDate0',
      'rulStartDate0',
      'pacNumber0',
      'pacUrl0',
      'cat3DispositionDate0',
      'cat3aDispositionDate0',
      'cat4DispositionDate0',
      'cat5DispositionDate0',
    ],
    [],
  );
  const watchedFieldValues = watch(trackedFieldNames);
  const resolveCurrentValue = useCallback(
    (fieldName) => {
      const watchedValueByKey = watchedFieldValues?.[fieldName];
      if (typeof watchedValueByKey !== 'undefined') {
        return watchedValueByKey;
      }

      const fieldIndex = trackedFieldNames.indexOf(fieldName);
      const watchedValueByIndex =
        fieldIndex > -1 && Array.isArray(watchedFieldValues)
          ? watchedFieldValues[fieldIndex]
          : undefined;
      if (typeof watchedValueByIndex !== 'undefined') {
        return watchedValueByIndex;
      }

      return methods.getValues(fieldName);
    },
    [trackedFieldNames, watchedFieldValues, methods],
  );

  const toDayStartValue = (dateValue) =>
    dateValue ? dayjs(dateValue).startOf('day').valueOf() : null;
  const toSelectValue = (optionValue) => optionValue?.value || null;
  const toTrimmedText = (textValue) => (textValue || '').trim();
  const normalizeFormValues = useCallback(
    (formValues) => ({
      status: toSelectValue(formValues?.status0),
      category: toSelectValue(formValues?.category0),
      component: toSelectValue(formValues?.component0),
      location: toSelectValue(formValues?.location0),
      part: toSelectValue(formValues?.part0) || '',
      damage: toSelectValue(formValues?.damage0),
      followupAction: toSelectValue(formValues?.followupAction0),
      followupDate: toDayStartValue(formValues?.followupDate0),
      rulStartDate: toDayStartValue(formValues?.rulStartDate0),
      pacNumber: toTrimmedText(formValues?.pacNumber0),
      pacUrl: toTrimmedText(formValues?.pacUrl0),
      cat3DispositionDate: toDayStartValue(formValues?.cat3DispositionDate0),
      cat3aDispositionDate: toDayStartValue(formValues?.cat3aDispositionDate0),
      cat4DispositionDate: toDayStartValue(formValues?.cat4DispositionDate0),
      cat5DispositionDate: toDayStartValue(formValues?.cat5DispositionDate0),
    }),
    [],
  );

  const hasAnyFieldChanged = useMemo(() => {
    if (!isUpdate || !exisitingDamage) return false;

    const currentValues = normalizeFormValues({
      status0: resolveCurrentValue('status0'),
      category0: resolveCurrentValue('category0'),
      component0: resolveCurrentValue('component0'),
      location0: resolveCurrentValue('location0'),
      part0: resolveCurrentValue('part0'),
      damage0: resolveCurrentValue('damage0'),
      followupAction0: resolveCurrentValue('followupAction0'),
      followupDate0: resolveCurrentValue('followupDate0'),
      rulStartDate0: resolveCurrentValue('rulStartDate0'),
      pacNumber0: resolveCurrentValue('pacNumber0'),
      pacUrl0: resolveCurrentValue('pacUrl0'),
      cat3DispositionDate0: resolveCurrentValue('cat3DispositionDate0'),
      cat3aDispositionDate0: resolveCurrentValue('cat3aDispositionDate0'),
      cat4DispositionDate0: resolveCurrentValue('cat4DispositionDate0'),
      cat5DispositionDate0: resolveCurrentValue('cat5DispositionDate0'),
    });

    const initialValues = normalizeFormValues(defaultFormValues);

    return Object.keys(initialValues).some((key) => currentValues[key] !== initialValues[key]);
  }, [isUpdate, exisitingDamage, defaultFormValues, normalizeFormValues, resolveCurrentValue]);

  const getFooter = useMemo(
    () => (
      <>
        {error && error?.response?.data?.description[0] && (
          <ErrorText style={{ width: '100%' }}>{error?.response?.data?.description[0]}</ErrorText>
        )}
        {updateError && updateError?.response?.data?.description && (
          <ErrorText style={{ width: '100%' }}>
            {updateError?.response?.data?.description}
          </ErrorText>
        )}
        <ButtonSection onClick={(e) => e.stopPropagation()}>
          <CancelButton disabled={isAddSaveLoading} onClick={onClose}>
            {t('approve_reject_dialog.cancel', 'Cancel')}
          </CancelButton>
          <ConfirmButton
            disabled={isAddSaveLoading || isEditSaveLoading || (isUpdate && !hasAnyFieldChanged)}
            primary
            onClick={handleSubmit(onSubmit)}
          >
            {t('case_panel.damage.save', 'Save')}
          </ConfirmButton>
        </ButtonSection>
      </>
    ),
    [
      error,
      updateError,
      isAddSaveLoading,
      onClose,
      t,
      isEditSaveLoading,
      hasAnyFieldChanged,
      isUpdate,
      handleSubmit,
      onSubmit,
    ],
  );

  const removeSelectedCompObjAtIndex = (i) => {
    setComponentValue((prev) => prev.filter((_, index) => index !== i));
    setPartValue((prev) => prev.filter((_, index) => index !== i));
    setselectedLocationOption((prev) => prev.filter((_, index) => index !== i));
    setselectedPartOption((prev) => prev.filter((_, index) => index !== i));
    setCategory((prev) => prev.filter((_, index) => index !== i));
  };
  const handleDeleteContainer = useCallback(
    (index) => {
      for (let i = index; i < newDamagesCounter - 1; i++) {
        FORM_FIELDS.map((field) => {
          methods.setValue(`${field}${i}`, methods.getValues(`${field}${i + 1}`));
        });
      }
      setNewDamagesCounter(newDamagesCounter - 1);
      removeSelectedCompObjAtIndex(index);
    },
    [newDamagesCounter, methods],
  );

  const getFieldsToShow = (category) => {
    switch (category) {
      case 'Category 3':
        return ['Category 3'];
      case 'Category 3a':
        return ['Category 3', 'Category 3a'];
      case 'Category 4':
        return ['Category 3', 'Category 3a', 'Category 4'];
      case 'Category 5':
        return ['Category 3', 'Category 3a', 'Category 4', 'Category 5'];
      default:
        return [];
    }
  };

  const fieldsToShow = exisitingDamage?.category ? getFieldsToShow(exisitingDamage?.category) : [];

  const getLocationDropDownOption = useCallback(
    (i, componentObject) => {
      if (componentObject) {
        const locationOptions = componentObject?.locations?.map((loc) => loc.value) || [];
        if (locationOptions) {
          const locationdDropdownOptions = locationOptions.map((item) => ({
            value: item,
            label: item,
          }));

          const sortedOptions = locationdDropdownOptions.sort((a, b) =>
            a.value.localeCompare(b.value),
          );
          setselectedLocationOption((prev) => {
            const updated = [...(prev || [])];
            while (updated.length <= i) updated.push(null);
            updated[i] = sortedOptions;
            return updated;
          });
        }
      }
    },
    [componentValue?.length],
  );

  const getPartDropDownOption = useCallback(
    (i, componentObject, value) => {
      if (componentObject) {
        const locations = componentObject?.locations.find((loc) => loc.value === value);
        const locationParts = locations?.parts?.map((part) => part.value);
        if (locationParts) {
          const partDropdownOptions = locationParts.map((item) => ({
            value: item,
            label: item,
          }));
          const sortedOptions = partDropdownOptions.sort((a, b) => a.value.localeCompare(b.value));
          setselectedPartOption((prev) => {
            const updated = [...(prev || [])];
            while (updated.length <= i) updated.push(null);
            updated[i] = sortedOptions;
            return updated;
          });
        }
        if (componentValue[i]?.value === 'Generator') {
          const locationParts = componentObject?.locations.find((loc) => loc.value === value);
          const locationDamage = locationParts?.parts.flatMap((part) =>
            part.damages.map((d) => d.value),
          );
          const locationDamageOptions = locationDamage?.map((item) => ({
            value: item,
            label: item,
          }));
          const sortedPartOptions = locationDamageOptions?.sort((a, b) =>
            a.value.localeCompare(b.value),
          );
          setselectedDamageOption((prev) => {
            const updated = [...(prev || [])];
            while (updated.length <= i) updated.push(null);
            updated[i] = sortedPartOptions;
            return updated;
          });
        }
      }
    },
    [componentValue],
  );

  const getDamageDropDownOption = useCallback(
    (i, componentObject, value) => {
      if (componentObject) {
        const parts = componentObject?.locations
          .flatMap((loc) => loc.parts)
          .find((p) => p.value === value);
        const damagesOptions = parts?.damages.map((d) => d.value) || [];
        if (damagesOptions) {
          const damageDropdownOptions = damagesOptions.map((item) => ({
            value: item,
            label: item,
          }));
          const sortedDamageOptions = damageDropdownOptions.sort((a, b) =>
            a.value.localeCompare(b.value),
          );
          const filteredDamageOptions = modelStarts2x3x
            ? sortedDamageOptions
            : sortedDamageOptions.filter((option) => option.value !== 'Main Shaft Drift');
          setselectedDamageOption((prev) => {
            const updated = [...(prev || [])];
            while (updated.length <= i) updated.push(null);
            updated[i] = filteredDamageOptions;
            return updated;
          });
        }
      }
    },
    [modelStarts2x3x],
  );

  const getComponent = useCallback(
    (value, i) => {
      if (value) {
        if (isUpdate) {
          setSelectedComponent(value);
          if (selectedComponent !== value) {
            setPartValue(null);
            setSelectedPart(null);
            setselectedPartOption([]);
            setselectedDamageOption([]);
            methods.setValue(`location${i}`, null);
            methods.setValue(`part${i}`, null);
            methods.setValue(`damage${i}`, null);
          }
        } else {
          const selectedCompObj =
            defectTreeData &&
            defectTreeData?.DamageHierarchy?.find((item) => item.component === value);
          setPartValue((prev) => {
            const updated = [...(prev || [])];
            updated[i] = null;
            return updated;
          });
          setselectedPartOption((prev) => {
            const updated = [...(prev || [])];
            updated[i] = [];
            return updated;
          });
          setselectedDamageOption((prev) => {
            const updated = [...(prev || [])];
            updated[i] = [];
            return updated;
          });
          getLocationDropDownOption(i, selectedCompObj);
        }
      }
    },
    [defectTreeData, getLocationDropDownOption, isUpdate, methods, selectedComponent],
  );

  const getLocation = useCallback(
    (value, i) => {
      if (value) {
        setSelectedLocation(value);
        if (isUpdate) {
          if (selectedLocation !== value) {
            setSelectedPart(null);
            setPartValue(null);
            setselectedDamageOption([]);
            methods.setValue(`part${i}`, null);
            methods.setValue(`damage${i}`, null);
          }
        } else {
          const selectedCompObj =
            defectTreeData &&
            defectTreeData?.DamageHierarchy?.find(
              (item) => item.component === componentValue[i]?.value,
            );
          setselectedDamageOption((prev) => {
            const updated = [...(prev || [])];
            updated[i] = [];
            return updated;
          });
          setPartValue((prev) => {
            const updated = [...(prev || [])];
            updated[i] = null;
            return updated;
          });
          getPartDropDownOption(i, selectedCompObj, value);
        }
      }
    },
    [componentValue, defectTreeData, getPartDropDownOption, isUpdate, methods, selectedLocation],
  );
  const getPart = useCallback(
    (i, value, selectedOption) => {
      if (isUpdate) {
        setPartValue(selectedOption);
        setSelectedPart(value);
        if (selectedPart !== value) {
          setselectedDamageOption([]);
          methods.setValue(`damage${i}`, null);
        }
      } else {
        const selectedCompObj =
          defectTreeData &&
          defectTreeData?.DamageHierarchy?.find(
            (item) => item.component === componentValue[i]?.value,
          );
        getDamageDropDownOption(i, selectedCompObj, value);
      }
    },
    [componentValue, defectTreeData, getDamageDropDownOption, isUpdate, methods, selectedPart],
  );

  const selectedCompObj =
    isUpdate &&
    defectTreeData &&
    defectTreeData?.DamageHierarchy?.find((item) => item.component === selectedComponent);

  useEffect(() => {
    if (isUpdate && selectedComponent === 'Generator') {
      setPartValue(null);
    }
  }, [selectedComponent, isUpdate]);

  useEffect(() => {
    if (isUpdate && exisitingDamage && exisitingDamage?.part) {
      setPartValue(setInitialSelectValue(exisitingDamage?.part));
    }
  }, [exisitingDamage, exisitingDamage?.part, isUpdate]);

  useEffect(() => {
    if (isUpdate && selectedComponent && selectedCompObj) {
      const locationOptions = selectedCompObj?.locations.map((loc) => loc.value) || [];
      if (locationOptions) {
        const locationdDropdownOptions = locationOptions.map((item) => ({
          value: item,
          label: item,
        }));
        setselectedLocationOption(
          locationdDropdownOptions?.sort(sorter('value', SortDirection.ASC)),
        );
      }
    }
  }, [selectedComponent, selectedCompObj, isUpdate]);

  useEffect(() => {
    if (isUpdate && selectedLocation && selectedCompObj) {
      const locations = selectedCompObj?.locations.find((loc) => loc.value === selectedLocation);
      const locationParts = locations?.parts?.map((part) => part.value);
      if (locationParts) {
        const partDropdownOptions = locationParts.map((item) => ({
          value: item,
          label: item,
        }));
        setselectedPartOption(partDropdownOptions?.sort(sorter('value', SortDirection.ASC)));
      }
      if (selectedComponent === 'Generator') {
        const locationParts = selectedCompObj?.locations.find(
          (loc) => loc.value === selectedLocation,
        );
        const locationDamage = locationParts?.parts.flatMap((part) =>
          part.damages.map((d) => d.value),
        );
        const locationDamageOptions = locationDamage?.map((item) => ({
          value: item,
          label: item,
        }));
        setselectedDamageOption(locationDamageOptions?.sort(sorter('value', SortDirection.ASC)));
      }
    }
  }, [selectedLocation, selectedCompObj, isUpdate, selectedComponent]);

  useEffect(() => {
    if (isUpdate && selectedPart && selectedCompObj) {
      const parts = selectedCompObj?.locations
        .flatMap((loc) => loc.parts)
        .find((p) => p.value === selectedPart);
      const damagesOptions = parts?.damages.map((d) => d.value) || [];
      if (damagesOptions) {
        const damageDropdownOptions = damagesOptions.map((item) => ({
          value: item,
          label: item,
        }));
        const filteredDamageOptions = modelStarts2x3x
          ? damageDropdownOptions
          : damageDropdownOptions.filter((option) => option.value !== 'Main Shaft Drift');
        setselectedDamageOption(filteredDamageOptions?.sort(sorter('value', SortDirection.ASC)));
      }
    }
  }, [selectedPart, selectedCompObj, isUpdate, modelStarts2x3x]);

  useEffect(() => {
    if (isUpdate) {
      setSelectedComponent(exisitingDamage?.component);
      setSelectedLocation(exisitingDamage?.location);
      setSelectedPart(exisitingDamage?.part);
    }
  }, [isUpdate]);

  return (
    <ScrollingContainer>
      <DialogScrollWrapper>
        <StyledDialog
          isOpen={true}
          header={
            isUpdate
              ? `${t('case_panel.damage.update_damage_header', 'UPDATE DAMAGE')}`
              : `${t('case_panel.damage.add_damage_header', 'ADD DAMAGE')}`
          }
          onClose={onClose}
          footer={getFooter}
          draggable={true}
        >
          <HeaderDetails>
            <SiteIcon />
            <div>{caseDetail.site?.name}</div>
            <StyledDetailsCurrentSeparator>/</StyledDetailsCurrentSeparator>
            <HeaderIcon icon={Icons.TURBINE} />
            &nbsp;
            <div className="asset-name">{caseDetail.asset?.name}</div>
          </HeaderDetails>
          <CaseDetails>
            <Icon icon={icon} size={20} color={globalColors.slate4} />
            <div>
              <div className="title">
                <Severity
                  size={12}
                  level={String(
                    entityType === EntityType.EVENT ? caseDetail.severity : caseDetail.priority,
                  ).toLowerCase()}
                />
                <span>
                  {caseDetail.description.length > 40
                    ? `${caseDetail.description.substring(0, 40)}...`
                    : caseDetail.description}
                </span>
              </div>
            </div>
          </CaseDetails>
          {!isUpdate && (
            <>
              <DamageDialogDivider />
              <SectionContainer>
                <EntityCollapsiblePanel
                  headerContent={
                    <h2>{t('case_panel.in_this_caseee', 'Attach existing damages')}</h2>
                  }
                  expanded={existingDamageData?.length || isLoading ? true : false}
                >
                  <DataLoader type={DataLoaderType.TABLE} isLoading={isLoading}>
                    <ExistingDamageDetailsTable
                      data={existingDamageData}
                      columns={visibleCols}
                      isLoading={isLoading}
                      existingDamage={selectedExistingDamages}
                      setExistingDamage={setSelectedExistingDamages}
                    />
                  </DataLoader>
                </EntityCollapsiblePanel>
              </SectionContainer>
            </>
          )}
          <DamageDialogDivider />
          {!isUpdate && (
            <ButtonWrapper>
              <AddIcon icon={showNewDamageDialog ? Icons.REMOVE : Icons.ADD} />
              <NewDamageButton onClick={() => setShowNewDamageDialog(!showNewDamageDialog)}>
                {t('case_panel.new-damage', 'NEW DAMAGE')}
              </NewDamageButton>
            </ButtonWrapper>
          )}

          {showNewDamageDialog &&
            [...Array(newDamagesCounter)].map((e, i) => (
              <>
                <NewDamageContainer
                  style={{
                    height:
                      isUpdate && DamgesStatus.HIGHER_THAN_CAT3.includes(exisitingDamage?.category)
                        ? '500px'
                        : '340px',
                  }}
                >
                  {isDefectTreeLoading && (
                    <DataLoaderContainer>
                      {/* <DataLoader type={DataLoaderType.SPINNER} isLoading={isLoading} /> */}
                    </DataLoaderContainer>
                  )}
                  {newDamagesCounter > 1 && (
                    <DeleteButtonWrapper>
                      <AddButton onClick={() => handleDeleteContainer(i)}>
                        <DeleteIcon icon={Icons.TRASH} />
                      </AddButton>
                    </DeleteButtonWrapper>
                  )}
                  <TaskProvider>
                    <FormProvider {...methods}>
                      <>
                        <AddDamageFormRow>
                          <Controller
                            name={`status${i}`}
                            rules={{ required: true }}
                            // defaultValue={null}
                            render={({ onChange, value }) => (
                              <StyledSelect
                                label={`${t('case_panel.damage.form.status', 'Status')}*`}
                                onChange={onChange}
                                value={value}
                                options={getOptions('status')}
                                name={`status${i}`}
                                error={methods.errors?.[`status${i}`]?.type}
                              />
                            )}
                          />

                          {!isUpdate || (isDamageAuthorized && isUpdate) ? (
                            <StyledCategoryContainer>
                              <Controller
                                name={`category${i}`}
                                rules={{ required: true }}
                                render={({ onChange, value }) => (
                                  <StyledSelect
                                    label={`${t('case_panel.damage.form.category', 'Category')}*`}
                                    onChange={(selectedOption) => {
                                      onChange(selectedOption);
                                      setCategory((prev) => {
                                        const updated = [...(prev || [])];
                                        while (updated.length <= i) updated.push(null);
                                        updated[i] = selectedOption;
                                        return updated;
                                      });
                                    }}
                                    value={value}
                                    options={getOptions('severity')}
                                    name={`category${i}`}
                                    error={methods.errors?.[`category${i}`]?.type}
                                  />
                                )}
                              />
                              <ErrorMessage
                                errors={methods.errors}
                                name={`category${i}`}
                                render={({ message }) => (
                                  <StyledErrorMessage role="alert">{message}</StyledErrorMessage>
                                )}
                              />
                            </StyledCategoryContainer>
                          ) : (
                            <StyledCategoryContainer>
                              <StyledLabel>{`${t(
                                'case_panel.damage.form.category',
                                'Category',
                              )}*`}</StyledLabel>
                              <div>{exisitingDamage?.category}</div>
                            </StyledCategoryContainer>
                          )}
                        </AddDamageFormRow>
                        <AddDamageFormRow>
                          <Controller
                            name={`component${i}`}
                            rules={{ required: true }}
                            render={({ onChange, value }) => (
                              <StyledSelect
                                label={`${t('case_panel.damage.form."component"', 'Component')}*`}
                                onChange={(selectedOption) => {
                                  onChange(selectedOption);
                                  if (!isUpdate) {
                                    setComponentValue((prev) => {
                                      const updated = [...(prev || [])];
                                      while (updated.length <= i) updated.push(null);
                                      updated[i] = selectedOption;
                                      return updated;
                                    });
                                  }
                                  getComponent(selectedOption?.value, i);
                                  if (!isUpdate) {
                                    methods.setValue(`location${i}`, null);
                                    methods.setValue(`part${i}`, null);
                                    methods.setValue(`damage${i}`, null);
                                  }
                                }}
                                value={value}
                                options={getOptions('component')}
                                name={`component${i}`}
                                error={methods.errors?.[`component${i}`]?.type}
                              />
                            )}
                          />
                          <Controller
                            name={`location${i}`}
                            rules={{ required: true }}
                            render={({ onChange, value }) => (
                              <StyledSelect
                                label={`${t('case_panel.damage.form."location"', 'Location')}*`}
                                onChange={(selectedOption) => {
                                  onChange(selectedOption);
                                  getLocation(selectedOption?.value, i);
                                  if (!isUpdate) {
                                    methods.setValue(`part${i}`, null);
                                    methods.setValue(`damage${i}`, null);
                                  }
                                }}
                                value={value}
                                options={
                                  isUpdate
                                    ? selectedLocationOption
                                    : selectedLocationOption?.[i] || null
                                }
                                name={`location${i}`}
                                error={methods.errors?.[`location${i}`]?.type}
                              />
                            )}
                          />
                          {watch(`component${i}`)?.value !== 'Generator' && (
                            <Controller
                              name={`part${i}`}
                              rules={{ required: true }}
                              render={({ onChange }) => (
                                <StyledSelect
                                  label={`${t('case_panel.damage.form.part', 'Part')}*`}
                                  onChange={(selectedOption) => {
                                    onChange(selectedOption);
                                    getPart(i, selectedOption?.value, selectedOption);
                                    if (!isUpdate) {
                                      methods.setValue(`damage${i}`, null);
                                      setPartValue((prev) => {
                                        const updated = [...(prev || [])];
                                        while (updated.length <= i) updated.push(null);
                                        updated[i] = selectedOption;
                                        return updated;
                                      });
                                    }
                                  }}
                                  value={isUpdate ? partValue : partValue?.[i] || null}
                                  options={
                                    isUpdate ? selectedPartOption : selectedPartOption?.[i] || null
                                  }
                                  name={`part${i}`}
                                  error={methods.errors?.[`part${i}`]?.type}
                                />
                              )}
                            />
                          )}
                        </AddDamageFormRow>
                        <AddDamageFormRow>
                          <Controller
                            name={`damage${i}`}
                            rules={{ required: true }}
                            render={({ onChange, value }) => (
                              <StyledSelect
                                label={`${t('case_panel.damage.form."damage"', 'Damage')}*`}
                                onChange={onChange}
                                value={value}
                                options={
                                  !isUpdate
                                    ? selectedDamageOption?.[i] || null
                                    : selectedDamageOption
                                }
                                name={`damage${i}`}
                                error={methods.errors?.[`damage${i}`]?.type}
                              />
                            )}
                          />
                          <Controller
                            name={`followupAction${i}`}
                            rules={{ required: true }}
                            render={({ onChange, value }) => (
                              <StyledSelect
                                label={`${t(
                                  'case_panel.damage.form.follow_up_action',
                                  'Required Action',
                                )}*`}
                                onChange={onChange}
                                value={value}
                                options={getOptions('action')}
                                name={`followupAction${i}`}
                                error={methods.errors?.[`followupAction${i}`]?.type}
                              />
                            )}
                          />
                        </AddDamageFormRow>
                        <AddDamageFormRow>
                          <>
                            {isUpdate &&
                              category[0]?.value !== 'Category 2' &&
                              (isDamageAuthorized &&
                              (isEngineeringAdmin ||
                                canEngineerUpdateEmptyRul(
                                  category[0]?.value || exisitingDamage?.category,
                                )) ? (
                                <StyledFormDateContainer>
                                  <div className="label">
                                    {`${t(
                                      'case_panel.damage.form."cat3_first_indication_date_damage"',
                                      'RUL Start Date',
                                    )}*`}
                                  </div>
                                  <DateMetaField
                                    metadata={{ required: false, readOnly: false, isInput: true }}
                                    name={`rulStartDate${i}`}
                                    max={new Date()}
                                    validators={[{ latestDate: 'latestDate' }]}
                                  />
                                  <ErrorMessage
                                    errors={methods.errors}
                                    name={`rulStartDate${i}`}
                                    render={({ message }) => (
                                      <StyledErrorMessage role="alert">
                                        {message}
                                      </StyledErrorMessage>
                                    )}
                                  />
                                </StyledFormDateContainer>
                              ) : (
                                <StyledCategoryContainer>
                                  <StyledLabel>
                                    {`${t(
                                      'case_panel.damage.form.cat3_first_indication_date_damage',
                                      'RUL Start Date',
                                    )}`}
                                  </StyledLabel>
                                  <div>
                                    {exisitingDamage?.rulStartDate
                                      ? dayjs(exisitingDamage.rulStartDate * 1000).format(
                                          'DD MMM YYYY',
                                        )
                                      : ''}
                                  </div>
                                </StyledCategoryContainer>
                              ))}

                            {!isUpdate && category[i]?.value !== 'Category 2' && (
                              <StyledFormDateContainer>
                                <div className="label">
                                  {`${t(
                                    'case_panel.damage.form."cat3_first_indication_date_damage"',
                                    'RUL Start Date',
                                  )}*`}
                                </div>
                                <DateMetaField
                                  metadata={{ required: false, readOnly: false, isInput: true }}
                                  name={`rulStartDate${i}`}
                                  max={new Date()}
                                  validators={[{ latestDate: 'latestDate' }]}
                                />
                                <ErrorMessage
                                  errors={methods.errors}
                                  name={`rulStartDate${i}`}
                                  render={({ message }) => (
                                    <StyledErrorMessage role="alert">{message}</StyledErrorMessage>
                                  )}
                                />
                              </StyledFormDateContainer>
                            )}
                            <StyledFormDateContainer>
                              <div>
                                <div className="label">
                                  {`${t('case_panel.damage.form.follow_up_date', 'Required Date')}`}
                                </div>
                                <DateMetaField
                                  metadata={{
                                    required: false,
                                    readOnly: false,
                                    isInput: true,
                                    isCustomErr: true,
                                  }}
                                  name={`followupDate${i}`}
                                  validators={[{ latestDate: 'latestDate' }]}
                                />
                              </div>
                            </StyledFormDateContainer>
                          </>
                        </AddDamageFormRow>
                        <AddDamageFormRow>
                          <PacWrapper>
                            <TextMetaField
                              label={t('case_panel.damage.form.pacNumber', 'PAC #')}
                              metadata={{ required: false }}
                              mask={'REN-########-####'}
                              maxLength={'17'}
                              name={`pacNumber${i}`}
                              placeholder={DamagesField.PAC_NUMBER}
                              tabIndex={1}
                              pacRules={{
                                required: false,
                                validate: (value) => {
                                  const trimmedValue = value?.trim();
                                  if (!trimmedValue.length) return true;
                                  const re = new RegExp('^REN-[0-9]{8}-[0-9]{4}$');
                                  return re.test(trimmedValue);
                                },
                              }}
                            />
                            {methods?.errors &&
                              methods?.errors[`pacNumber${i}`]?.type === 'validate' && (
                                <ErrorText>Invalid PAC number.</ErrorText>
                              )}
                          </PacWrapper>
                          <PacWrapper>
                            <TextMetaField
                              label={t('form.pacUrl', 'PAC URL')}
                              placeholder={DamagesField.PAC_URL}
                              metadata={{ required: false }}
                              name={`pacUrl${i}`}
                              tabIndex={2}
                              className={'pac-url'}
                              pacRules={{
                                required: false,
                                validate: (value) => {
                                  const trimmedValue = value?.toLowerCase()?.trim();
                                  if (!trimmedValue.length) return true;
                                  let newStr = trimmedValue.replace(/^http(s?):\/\//i, '');
                                  return newStr.startsWith('gepowerpac.service-now.com');
                                },
                              }}
                            />
                            {methods?.errors &&
                              methods?.errors[`pacUrl${i}`]?.type === 'validate' && (
                                <ErrorText>Invalid PAC url.</ErrorText>
                              )}
                          </PacWrapper>
                        </AddDamageFormRow>
                        {isUpdate && (
                          <>
                            <AddDamageFormRow>
                              {fieldsToShow.includes('Category 3') && (
                                <Cat3Wrapper>
                                  {isDamageAuthorized && isEngineeringAdmin ? (
                                    <StyledFormDateContainer>
                                      <div style={{ width: '9rem' }} className="label">
                                        {`${t(
                                          'case_panel.damage.form.cat3_logged_date',
                                          'Category 3 Disposition Date',
                                        )}`}
                                      </div>
                                      <DateMetaField
                                        metadata={{
                                          required: false,
                                          readOnly: false,
                                          isInput: true,
                                        }}
                                        name={`cat3DispositionDate${i}`}
                                        max={new Date()}
                                        validators={[{ latestDate: 'latestDate' }]}
                                      />
                                      <ErrorMessage
                                        errors={methods.errors}
                                        name={`cat3DispositionDate${i}`}
                                        render={({ message }) => (
                                          <StyledErrorMessage role="alert">
                                            {message}
                                          </StyledErrorMessage>
                                        )}
                                      />
                                    </StyledFormDateContainer>
                                  ) : (
                                    <StyledCategoryContainer>
                                      <StyledLabel>{`${t(
                                        'case_panel.damage.form.cat3_logged_date',
                                        'Category 3 Disposition Date',
                                      )}`}</StyledLabel>
                                      <div>
                                        {exisitingDamage?.cat3DispositionDate
                                          ? dayjs(
                                              exisitingDamage.cat3DispositionDate * 1000,
                                            ).format('DD MMM YYYY')
                                          : ''}
                                      </div>
                                    </StyledCategoryContainer>
                                  )}
                                </Cat3Wrapper>
                              )}
                              {fieldsToShow.includes('Category 3a') && (
                                <Cat3Wrapper style={{ marginLeft: '1rem' }}>
                                  {isDamageAuthorized && isEngineeringAdmin ? (
                                    <StyledFormDateContainer>
                                      <div style={{ width: '9rem' }} className="label">
                                        {`${t(
                                          'case_panel.damage.form."cat3a_logged_date"',
                                          'Category 3a Disposition Date',
                                        )}`}
                                      </div>
                                      <DateMetaField
                                        metadata={{
                                          required: false,
                                          readOnly: false,
                                          isInput: true,
                                        }}
                                        name={`cat3aDispositionDate${i}`}
                                        max={new Date()}
                                        validators={[{ latestDate: 'latestDate' }]}
                                      />
                                    </StyledFormDateContainer>
                                  ) : (
                                    <StyledCategoryContainer>
                                      <StyledLabel>{`${t(
                                        'case_panel.damage.form.cat3a_logged_date',
                                        'Category 3a Disposition Date',
                                      )}`}</StyledLabel>
                                      <div>
                                        {exisitingDamage?.cat3aDispositionDate
                                          ? dayjs(
                                              exisitingDamage.cat3aDispositionDate * 1000,
                                            ).format('DD MMM YYYY')
                                          : ''}
                                      </div>
                                    </StyledCategoryContainer>
                                  )}
                                </Cat3Wrapper>
                              )}
                            </AddDamageFormRow>
                            <AddDamageFormRow>
                              {fieldsToShow.includes('Category 4') && (
                                <Cat3Wrapper>
                                  {isDamageAuthorized && isEngineeringAdmin ? (
                                    <StyledFormDateContainer>
                                      <div style={{ width: '9rem' }} className="label">
                                        {`${t(
                                          'case_panel.damage.form."cat4_logged_date"',
                                          'Category 4 Disposition Date',
                                        )}`}
                                      </div>
                                      <DateMetaField
                                        metadata={{
                                          required: false,
                                          readOnly: false,
                                          isInput: true,
                                        }}
                                        name={`cat4DispositionDate${i}`}
                                        max={new Date()}
                                        validators={[{ latestDate: 'latestDate' }]}
                                      />
                                    </StyledFormDateContainer>
                                  ) : (
                                    <StyledCategoryContainer>
                                      <StyledLabel>{`${t(
                                        'case_panel.damage.form.cat4_logged_date',
                                        'Category 4 Disposition Date',
                                      )}`}</StyledLabel>
                                      <div>
                                        {exisitingDamage?.cat4DispositionDate
                                          ? dayjs(
                                              exisitingDamage.cat4DispositionDate * 1000,
                                            ).format('DD MMM YYYY')
                                          : ''}
                                      </div>
                                    </StyledCategoryContainer>
                                  )}
                                </Cat3Wrapper>
                              )}
                              {fieldsToShow.includes('Category 5') && (
                                <Cat3Wrapper style={{ marginLeft: '1rem' }}>
                                  {isDamageAuthorized && isEngineeringAdmin ? (
                                    <StyledFormDateContainer>
                                      <div style={{ width: '9rem' }} className="label">
                                        {`${t(
                                          'case_panel.damage.form."cat5_logged_date"',
                                          'Category 5 Disposition Date',
                                        )}`}
                                      </div>
                                      <DateMetaField
                                        metadata={{
                                          required: false,
                                          readOnly: false,
                                          isInput: true,
                                        }}
                                        name={`cat5DispositionDate${i}`}
                                        max={new Date()}
                                        validators={[{ latestDate: 'latestDate' }]}
                                      />
                                    </StyledFormDateContainer>
                                  ) : (
                                    <StyledCategoryContainer>
                                      <StyledLabel>{`${t(
                                        'case_panel.damage.form.cat5_logged_date',
                                        'Category 5 Disposition Date',
                                      )}`}</StyledLabel>
                                      <div>
                                        {exisitingDamage?.cat5DispositionDate
                                          ? dayjs(
                                              exisitingDamage.cat5DispositionDate * 1000,
                                            ).format('DD MMM YYYY')
                                          : ''}
                                      </div>
                                    </StyledCategoryContainer>
                                  )}
                                </Cat3Wrapper>
                              )}
                            </AddDamageFormRow>
                          </>
                        )}
                      </>
                    </FormProvider>
                  </TaskProvider>
                </NewDamageContainer>
              </>
            ))}
          {!isUpdate && showNewDamageDialog && (
            <AddButtonWrapper>
              <AddIcon icon={Icons.ADD} />
              <AddButton onClick={() => setNewDamagesCounter(newDamagesCounter + 1)}>
                {t('case_panel."add"', 'ADD')}
              </AddButton>
            </AddButtonWrapper>
          )}
        </StyledDialog>
      </DialogScrollWrapper>
    </ScrollingContainer>
  );
};

export default AddDamageDialog;

AddDamageDialog.propTypes = {
  selectedMalEvents: PropTypes.array,
  onClose: PropTypes.func,
  onApproveMalEvents: PropTypes.func,
  caseDetail: PropTypes.instanceOf(Object).isRequired,
  entityType: PropTypes.oneOf([EntityType.ANOMALY, EntityType.CASE]).isRequired,
  metadata: PropTypes.object.isRequired,
  isUpdate: PropTypes.boolean,
  exisitingDamage: PropTypes.object,
};
